import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';

import { typeLabel } from './agenda-adapter';

import type { JSX } from 'react';

/**
 * NoShowModal — marca falta (Brief 2B §12.6). Idioma de modal do atendente.
 * `generatePendency` default ON → o backend gera a Pendency de follow-up que
 * cai na Tela 3 (D6). Motivo obrigatório.
 *
 * `appt` é ESTRUTURAL, não `AppointmentWireItem`: o modal lê exatamente dois campos
 * (`type` e `title`). Estreitar a prop para o que ele de fato usa deixa o mesmo modal
 * servir a Agenda (wire) e o cartão da Conversa em curso (fixture do painel) sem
 * duplicar a UI de falta — que é onde mora a regra de motivo obrigatório.
 */
export function NoShowModal({
  open,
  appt,
  submitting,
  onClose,
  onConfirm,
}: {
  open: boolean;
  appt: { type: string; title: string };
  submitting: boolean;
  onClose: () => void;
  onConfirm: (p: { reason: string; generatePendency: boolean }) => void;
}): JSX.Element | null {
  const [reason, setReason] = useState('');
  const [generatePendency, setGeneratePendency] = useState(true);

  if (!open) return null;
  const canConfirm = reason.trim().length > 0 && !submitting;

  return (
    <div
      role="dialog"
      aria-label="Registrar falta"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="w-full max-w-lg rounded-lg p-6 shadow-xl"
        style={{ background: 'var(--bg-surface)' }}
      >
        <h2 className="mb-1 text-lg font-semibold">Registrar falta do cliente</h2>
        <p className="mb-3 text-sm" style={{ color: 'var(--fg-3)' }}>
          {typeLabel(appt.type)} · {appt.title}
        </p>

        <div
          className="mb-3 flex items-start gap-2 rounded p-2 text-sm"
          style={{
            color: 'var(--warning-fg)',
            background: 'var(--warning-bg)',
            border: '1px solid var(--warning-border)',
          }}
        >
          <AlertTriangle size={14} aria-hidden />
          <span>
            Cliente não compareceu / não confirmou. Registra a falta e (opcional) gera um follow-up
            automático.
          </span>
        </div>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium">Motivo (obrigatório)</span>
          <textarea
            className="w-full rounded border px-2 py-1"
            style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
            rows={4}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex.: cliente não compareceu; técnico esteve no local mas a casa estava fechada."
          />
        </label>

        <label className="mb-3 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={generatePendency}
            onChange={(e) => setGeneratePendency(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            <strong>Gerar pendência de follow-up automática</strong>
            <br />
            <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>
              Aparece na sua caixa de Pendências para reagendar o contato.
            </span>
          </span>
        </label>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border px-4 py-2 text-sm"
            style={{ borderColor: 'var(--border-default)' }}
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--destructive-solid"
            onClick={() => onConfirm({ reason: reason.trim(), generatePendency })}
            disabled={!canConfirm}
          >
            {submitting ? 'Registrando…' : 'Confirmar falta'}
          </button>
        </div>
      </div>
    </div>
  );
}
